import type { AffinityTargetable, IAffinityTargetable } from './targetable_affinity';
import type {
  AgeDemographicTargetable,
  IAgeDemographicTargetable,
} from './targetable_age_demographic';
import type { EmailListTargetable, IEmailListTargetable } from './targetable_email_list';
import type { FactualTargetable, IFactualTargetable } from './targetable_factual';
import type { GeoTargetable, IGeoTargetable } from './targetable_geo';
import type { GeoSetTargetable, IGeoSetTargetable } from './targetable_geo_set';
import type { GoogleGeoTargetable, IGoogleGeoTargetable } from './targetable_google_ads_geo';
import type { IInterestTargetable, InterestTargetable } from './targetable_interest';
import type { ILookalikeTargetable, LookalikeTargetable } from './targetable_lookalike';
import type {
  IOmniLookalikeTargetable,
  OmniLookalikeTargetable,
} from './targetable_omni_lookalike';
import type { IOmniSliceTargetable, OmniSliceTargetable } from './targetable_omni_slice';
import type { ISearchTargetable, SearchTargetable } from './targetable_search';
import type { ISeedSegmentTargetable, SeedSegmentTargetable } from './targetable_seed_segment';
import type { Targetable } from './targetables';

export enum TargetableClass {
  email_list = 'Targetable.EmailListTargetable',
  geo = 'Targetable.GeoTargetable',
  geo_set = 'Targetable.GeoSetTargetable',
  factual = 'Targetable.FactualTargetable',
  lookalike = 'Targetable.LookalikeTargetable',
  seed_segment = 'Targetable.SeedSegmentTargetable',
  affinity = 'Targetable.AffinityTargetable',
  search = 'Targetable.SearchTargetable',
  segment = 'PersonSegment',
  google_ads_geo = 'Targetable.GoogleGeoTargetable',
  age_demographic = 'Targetable.TradedeskAgeTargetable',
  interest = 'Targetable.TradedeskInterestTargetable',
  omni_slice = 'Targetable.TradedeskOmniSliceTargetable',
  omni_lookalike = 'Targetable.TradedeskOmniLookalikeTargetable',
}

export type TTargetableClassMap = {
  [TargetableClass.email_list]: EmailListTargetable;
  [TargetableClass.geo]: GeoTargetable;
  [TargetableClass.geo_set]: GeoSetTargetable;
  [TargetableClass.factual]: FactualTargetable;
  [TargetableClass.lookalike]: LookalikeTargetable;
  [TargetableClass.seed_segment]: SeedSegmentTargetable;
  [TargetableClass.affinity]: AffinityTargetable;
  [TargetableClass.search]: SearchTargetable;
  [TargetableClass.google_ads_geo]: GoogleGeoTargetable;
  [TargetableClass.age_demographic]: AgeDemographicTargetable;
  [TargetableClass.interest]: InterestTargetable;
  [TargetableClass.omni_slice]: OmniSliceTargetable;
  [TargetableClass.omni_lookalike]: OmniLookalikeTargetable;
};

/* 
  TTargetableClass is a type that returns the corresponding targetable class for a given targetable class, 
  instead of returning the generic Targetable class.
*/
export type TTargetableClass<T> = T extends keyof TTargetableClassMap
  ? TTargetableClassMap[T]
  : Targetable;

// ITargetable is a union type of all specific targetable interfaces
// This provides type safety while allowing any targetable type to be used
export type ITargetable =
  | IAffinityTargetable
  | IAgeDemographicTargetable
  | IEmailListTargetable
  | IFactualTargetable
  | IGeoTargetable
  | IGeoSetTargetable
  | IGoogleGeoTargetable
  | IInterestTargetable
  | ILookalikeTargetable
  | IOmniLookalikeTargetable
  | IOmniSliceTargetable
  | ISearchTargetable
  | ISeedSegmentTargetable;
